import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, ArrowBack, CheckCircle, Delete } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import produccionService, {
  ConsumoPrevistoDto,
  PreviewConfirmacionProduccionDto,
  ProduccionOrdenDto,
} from '../../services/produccionService';
import productoService, { OPTIONS_PRODUCTO_OPERATIVO } from '../../services/productoService';
import RecetaArticuloSearchDialog, {
  RecetaArticuloSeleccionado,
} from './RecetaArticuloSearchDialog';
import LoteLineaButton from '../../components/lotes/LoteLineaButton';
import {
  extractLoteFlagsFromProducto,
  mensajeValidacionLotes,
} from '../../utils/loteLineaHelpers';
import type { LoteAsignacion } from '../../services/loteService';

type LineaConsumoEditable = {
  key: string;
  codigoArticulo: string;
  descripcion?: string | null;
  cantidadReceta: number;
  cantidadEstimada: number;
  cantidad: number;
  stockDisponible: number;
  suficiente: boolean;
  controlaStock: boolean;
  esAgregado: boolean;
  controlLoteSerie?: string | null;
  requiereVencimiento?: boolean | null;
  lotes?: LoteAsignacion[];
};

let lineaKeySeq = 0;
const nextKey = () => `c-${++lineaKeySeq}`;

const fromPreview = (c: ConsumoPrevistoDto): LineaConsumoEditable => ({
  key: nextKey(),
  codigoArticulo: c.codigoArticulo,
  descripcion: c.descripcion,
  cantidadReceta: c.cantidadReceta,
  cantidadEstimada: c.cantidadEstimada ?? c.cantidadRequerida,
  cantidad: c.cantidadRequerida,
  stockDisponible: c.stockDisponible,
  suficiente: c.suficiente,
  controlaStock: c.controlaStock !== false,
  esAgregado: Boolean(c.esAgregado),
});

const ConfirmarProduccion: React.FC = () => {
  const { id } = useParams();
  const codigo = Number(id);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [orden, setOrden] = useState<ProduccionOrdenDto | null>(null);
  const [cantidadProducida, setCantidadProducida] = useState(1);
  const [cantidadRechazada, setCantidadRechazada] = useState(0);
  const [observaciones, setObservaciones] = useState('');
  const [preview, setPreview] = useState<PreviewConfirmacionProduccionDto | null>(null);
  const [lineas, setLineas] = useState<LineaConsumoEditable[]>([]);
  const [loading, setLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [ptControlLoteSerie, setPtControlLoteSerie] = useState('N');
  const [ptRequiereVencimiento, setPtRequiereVencimiento] = useState(false);
  const [ptLotes, setPtLotes] = useState<LoteAsignacion[]>([]);

  useEffect(() => {
    if (!codigo) return;
    produccionService
      .getOrden(codigo)
      .then(async (data) => {
        setOrden(data);
        setCantidadProducida(data.cantidad ?? 1);
        setObservaciones(data.observaciones ?? '');
        try {
          const pt = await productoService.getProducto(data.codigoArticulo, OPTIONS_PRODUCTO_OPERATIVO);
          const flags = extractLoteFlagsFromProducto(pt);
          setPtControlLoteSerie(flags.controlLoteSerie);
          setPtRequiereVencimiento(flags.requiereVencimiento);
        } catch {
          setPtControlLoteSerie('N');
          setPtRequiereVencimiento(false);
        }
      })
      .catch(() => {
        enqueueSnackbar('No se pudo cargar la orden', { variant: 'error' });
        navigate('/produccion/ordenes');
      });
  }, [codigo, enqueueSnackbar, navigate]);

  const enrichLineasConLote = async (rows: LineaConsumoEditable[]): Promise<LineaConsumoEditable[]> => {
    const codes = Array.from(new Set(rows.map((r) => r.codigoArticulo.trim()).filter(Boolean)));
    if (codes.length === 0) return rows;
    const results = await Promise.allSettled(
      codes.map((c) => productoService.getProducto(c, OPTIONS_PRODUCTO_OPERATIVO)),
    );
    const byCode = new Map<string, ReturnType<typeof extractLoteFlagsFromProducto>>();
    codes.forEach((c, i) => {
      const res = results[i];
      if (res.status === 'fulfilled') byCode.set(c.toUpperCase(), extractLoteFlagsFromProducto(res.value));
    });
    return rows.map((r) => {
      const flags = byCode.get(r.codigoArticulo.trim().toUpperCase());
      if (!flags) return r;
      return {
        ...r,
        controlLoteSerie: r.controlLoteSerie || flags.controlLoteSerie,
        requiereVencimiento: r.requiereVencimiento ?? flags.requiereVencimiento,
      };
    });
  };

  const buildDto = (incluirConsumos: boolean) => ({
    cantidadProducida,
    cantidadRechazada,
    observaciones,
    lotes: ptLotes,
    ...(incluirConsumos && lineas.length > 0
      ? {
          consumos: lineas.map((l) => ({
            codigoArticulo: l.codigoArticulo,
            cantidad: Number(l.cantidad) || 0,
            lotes: l.lotes,
          })),
        }
      : {}),
  });

  const applyPreview = async (data: PreviewConfirmacionProduccionDto, preserveQty: boolean) => {
    setPreview(data);
    if (!preserveQty || lineas.length === 0) {
      const mapped = await enrichLineasConLote(data.consumos.map(fromPreview));
      setLineas(mapped);
      return;
    }
    const byCode = new Map(data.consumos.map((c) => [c.codigoArticulo.trim().toUpperCase(), c]));
    setLineas((prev) =>
      prev.map((l) => {
        const p = byCode.get(l.codigoArticulo.trim().toUpperCase());
        if (!p) return l;
        return {
          ...l,
          descripcion: p.descripcion,
          cantidadReceta: p.cantidadReceta,
          cantidadEstimada: p.cantidadEstimada ?? p.cantidadRequerida,
          stockDisponible: p.stockDisponible,
          suficiente: p.controlaStock === false || p.stockDisponible >= (Number(l.cantidad) || 0),
          controlaStock: p.controlaStock !== false,
          esAgregado: Boolean(p.esAgregado),
        };
      }),
    );
  };

  const runPreview = async (opts?: { resetFromReceta?: boolean }) => {
    try {
      setLoading(true);
      const reset = opts?.resetFromReceta === true || lineas.length === 0;
      const data = await produccionService.previewConfirmacion(codigo, buildDto(!reset));
      await applyPreview(data, !reset);
    } catch (error: unknown) {
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Error al calcular el consumo';
      enqueueSnackbar(msg, { variant: 'error' });
      if (opts?.resetFromReceta) {
        setPreview(null);
        setLineas([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (lineas.length === 0) {
      enqueueSnackbar('Calculá el consumo antes de confirmar', { variant: 'warning' });
      return;
    }
    const loteErrConsumo = mensajeValidacionLotes(lineas);
    if (loteErrConsumo) {
      enqueueSnackbar(loteErrConsumo, { variant: 'error' });
      return;
    }
    const loteErrPt = mensajeValidacionLotes([
      {
        codigoArticulo: orden?.codigoArticulo,
        cantidad: cantidadProducida,
        controlLoteSerie: ptControlLoteSerie,
        lotes: ptLotes,
      },
    ]);
    if (loteErrPt) {
      enqueueSnackbar(loteErrPt, { variant: 'error' });
      return;
    }
    if (!window.confirm('¿Confirmar la producción? Se consumirán materias primas y se ingresará el PT.')) return;
    try {
      setLoading(true);
      const result = await produccionService.confirmar(codigo, buildDto(true));
      enqueueSnackbar(
        `Producción confirmada. Costo unitario: ${result.costoUnitario.toFixed(4)}`,
        { variant: 'success' },
      );
      navigate(`/produccion/ordenes/${codigo}/desvios`);
    } catch (error: unknown) {
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Error al confirmar la producción';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const updateCantidad = (key: string, value: number) => {
    setLineas((prev) =>
      prev.map((l) => {
        if (l.key !== key) return l;
        const cantidad = Number.isFinite(value) ? value : 0;
        const suficiente = !l.controlaStock || l.stockDisponible >= cantidad;
        return { ...l, cantidad, suficiente };
      }),
    );
    setPreview(null);
  };

  const removeLinea = (key: string) => {
    setLineas((prev) => prev.filter((l) => l.key !== key));
    setPreview(null);
  };

  const handleAddArticulo = async (item: RecetaArticuloSeleccionado) => {
    setAddDialogOpen(false);
    const codigoNorm = item.codigoArticulo.trim().toUpperCase();
    if (lineas.some((l) => l.codigoArticulo.trim().toUpperCase() === codigoNorm)) {
      enqueueSnackbar('El artículo ya está en la grilla', { variant: 'warning' });
      return;
    }
    let flags = { controlLoteSerie: 'N', requiereVencimiento: false };
    try {
      const p = await productoService.getProducto(item.codigoArticulo.trim(), OPTIONS_PRODUCTO_OPERATIVO);
      flags = extractLoteFlagsFromProducto(p);
    } catch {
      /* ignore */
    }
    setLineas((prev) => [
      ...prev,
      {
        key: nextKey(),
        codigoArticulo: item.codigoArticulo.trim(),
        descripcion: item.descripcion,
        cantidadReceta: 0,
        cantidadEstimada: 0,
        cantidad: 0,
        stockDisponible: 0,
        suficiente: true,
        controlaStock: true,
        esAgregado: true,
        controlLoteSerie: flags.controlLoteSerie,
        requiereVencimiento: flags.requiereVencimiento,
      },
    ]);
    setPreview(null);
    enqueueSnackbar('Artículo agregado. Indicá la cantidad y recalculá stock/costos.', {
      variant: 'info',
    });
  };

  const hayFaltante = lineas.some((c) => c.controlaStock && !c.suficiente);
  const puedeConfirmar = lineas.length > 0 && !hayFaltante && !loading;
  const depOrigen = Number(orden?.depositoOrigen) || 0;
  const depDestino = Number(orden?.depositoDestino) || 0;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Button startIcon={<ArrowBack />} onClick={() => navigate(`/produccion/ordenes/${codigo}`)} sx={{ textTransform: 'none' }}>
              Orden
            </Button>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Confirmar producción #{codigo}
            </Typography>
          </Stack>

          {orden && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
              <Typography variant="body2" color="text.secondary">
                {orden.codigoArticulo} — {orden.descripcion} | Receta: {orden.codigoReceta ?? '—'} | Estado:{' '}
                {orden.estado}
              </Typography>
              <LoteLineaButton
                controlLoteSerie={ptControlLoteSerie}
                requiereVencimiento={ptRequiereVencimiento}
                codigoArticulo={orden.codigoArticulo}
                cantidad={Number(cantidadProducida) || 0}
                nroUbicacion={depDestino}
                mode="ingreso"
                lotes={ptLotes}
                onChange={setPtLotes}
              />
            </Stack>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Cantidad producida"
              type="number"
              value={cantidadProducida}
              onChange={(e) => {
                setCantidadProducida(Number(e.target.value));
                setLineas([]);
                setPreview(null);
                setPtLotes([]);
              }}
              fullWidth
            />
            <TextField
              label="Cantidad rechazada"
              type="number"
              value={cantidadRechazada}
              onChange={(e) => setCantidadRechazada(Number(e.target.value))}
              fullWidth
            />
            <TextField
              label="Observaciones"
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              fullWidth
            />
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              onClick={() => void runPreview({ resetFromReceta: true })}
              disabled={loading}
            >
              Calcular consumo (receta)
            </Button>
            <Button
              variant="outlined"
              onClick={() => void runPreview({ resetFromReceta: false })}
              disabled={loading || lineas.length === 0}
            >
              Recalcular stock y costos
            </Button>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setAddDialogOpen(true)}
              disabled={loading || lineas.length === 0}
            >
              Agregar componente
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              onClick={() => void handleConfirm()}
              disabled={!puedeConfirmar || !preview}
            >
              Confirmar
            </Button>
          </Stack>

          {hayFaltante && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Hay materias primas con stock insuficiente. Recalculá o ajustá las cantidades antes de confirmar.
            </Alert>
          )}

          {lineas.length > 0 && (
            <Stack spacing={2}>
              {preview && (
                <Typography variant="body2">
                  Costo materiales: {preview.costoMaterialesEstimado.toFixed(4)} | Mano de obra:{' '}
                  {preview.manoObraEstimada.toFixed(4)} | Costo unitario estimado:{' '}
                  {preview.costoUnitarioEstimado.toFixed(4)}
                </Typography>
              )}
              {!preview && (
                <Alert severity="info">
                  Editaste consumos. Usá «Recalcular stock y costos» para actualizar disponibilidad y costos antes de
                  confirmar.
                </Alert>
              )}
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Artículo</TableCell>
                    <TableCell align="right">Cant. receta</TableCell>
                    <TableCell align="right">Estimado</TableCell>
                    <TableCell align="right">Consumo real</TableCell>
                    <TableCell align="right">Disponible</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Lote</TableCell>
                    <TableCell align="center" width={56} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineas.map((c) => (
                    <TableRow key={c.key}>
                      <TableCell>
                        {c.codigoArticulo} — {c.descripcion}
                        {c.esAgregado ? ' (agregado)' : ''}
                      </TableCell>
                      <TableCell align="right">{c.cantidadReceta}</TableCell>
                      <TableCell align="right">{c.cantidadEstimada}</TableCell>
                      <TableCell align="right" sx={{ minWidth: 120 }}>
                        <TextField
                          type="number"
                          size="small"
                          value={c.cantidad}
                          onChange={(e) => updateCantidad(c.key, Number(e.target.value))}
                          inputProps={{ min: 0, step: 'any' }}
                          sx={{ width: 110 }}
                        />
                      </TableCell>
                      <TableCell align="right">{c.controlaStock ? c.stockDisponible : '—'}</TableCell>
                      <TableCell>
                        {!c.controlaStock
                          ? 'Sin inventario'
                          : c.suficiente
                            ? 'OK'
                            : 'Faltante'}
                      </TableCell>
                      <TableCell>
                        <LoteLineaButton
                          controlLoteSerie={c.controlLoteSerie}
                          requiereVencimiento={c.requiereVencimiento}
                          codigoArticulo={c.codigoArticulo}
                          cantidad={Number(c.cantidad) || 0}
                          nroUbicacion={depOrigen}
                          mode="egreso"
                          lotes={c.lotes}
                          onChange={(lotes) =>
                            setLineas((prev) => prev.map((l) => (l.key === c.key ? { ...l, lotes } : l)))
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Quitar componente">
                          <IconButton size="small" color="error" onClick={() => removeLinea(c.key)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Stack>
          )}
        </CardContent>
      </Card>

      <RecetaArticuloSearchDialog
        open={addDialogOpen}
        title="Agregar componente de consumo"
        onClose={() => setAddDialogOpen(false)}
        onSelect={(item) => void handleAddArticulo(item)}
      />
    </Box>
  );
};

export default ConfirmarProduccion;
